import React, { useEffect, useMemo, useState } from 'react';
import { characterApi } from '../../services/characterApi';
import factionService from '../../services/factionService';
import threadService from '../../services/threadService';
import styles from './ThreadEditPanel.module.css';

const ThreadEditPanel = ({
  thread,
  universeSlug,
  onSaved,
  onCancel,
}) => {
  const isRoleplay = Boolean(thread?.isRoleplay || thread?.type === 'roleplay');
  const [title, setTitle] = useState(thread?.title || '');
  const [participantIds, setParticipantIds] = useState([]);
  const [factionIds, setFactionIds] = useState([]);
  const [universeCharacters, setUniverseCharacters] = useState([]);
  const [factions, setFactions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setTitle(thread?.title || '');
    setParticipantIds((thread?.participants || []).map((p) => String(p.id)));
    setFactionIds((thread?.factions || []).map((f) => String(f.id)));
  }, [thread]);

  useEffect(() => {
    if (!isRoleplay || !universeSlug) return undefined;

    let cancelled = false;
    setLoadingOptions(true);

    Promise.all([
      characterApi.getUniverseValidatedCharacters(universeSlug),
      factionService.getFactions({ universe: universeSlug }),
    ])
      .then(([charactersData, factionsData]) => {
        if (cancelled) return;
        setUniverseCharacters(Array.isArray(charactersData?.items) ? charactersData.items : []);
        setFactions(Array.isArray(factionsData?.factions) ? factionsData.factions : []);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Impossible de charger les personnages et factions disponibles.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRoleplay, universeSlug]);

  const participantSet = useMemo(
    () => new Set(participantIds.map((id) => String(id))),
    [participantIds],
  );

  const factionSet = useMemo(
    () => new Set(factionIds.map((id) => String(id))),
    [factionIds],
  );

  const toggleParticipant = (id) => {
    const normalized = String(id);
    setParticipantIds((prev) => (
      participantSet.has(normalized)
        ? prev.filter((item) => String(item) !== normalized)
        : [...prev, normalized]
    ));
  };

  const toggleFaction = (id) => {
    const normalized = String(id);
    setFactionIds((prev) => (
      factionSet.has(normalized)
        ? prev.filter((item) => String(item) !== normalized)
        : [...prev, normalized]
    ));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle || trimmedTitle.length < 3) {
      setError('Le titre doit contenir au moins 3 caractères.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = { title: trimmedTitle };
      if (isRoleplay) {
        payload.participantCharacterIds = participantIds.map(Number).filter(Boolean);
        payload.factionIds = factionIds.map(Number).filter(Boolean);
      }

      const result = await threadService.updateThread(thread.threadId, payload);
      onSaved?.(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour du thread');
    } finally {
      setSaving(false);
    }
  };

  const getCharacterLabel = (character) => (
    character.user?.pseudo
      ? `${character.name} (@${character.user.pseudo})`
      : character.name
  );

  return (
    <form className={styles.panel} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h3 className={styles.title}>Modifier le thread</h3>
        <button type="button" className={styles.closeBtn} onClick={onCancel}>
          Fermer
        </button>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="thread-edit-title">Titre</label>
        <input
          id="thread-edit-title"
          type="text"
          className={styles.input}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={150}
          required
        />
      </div>

      {isRoleplay && (
        <>
          <div className={styles.field}>
            <span className={styles.label}>Participants RP</span>
            {loadingOptions ? (
              <p className={styles.hint}>Chargement des personnages…</p>
            ) : (
              <div className={styles.optionList}>
                {universeCharacters.length > 0 ? universeCharacters.map((character) => {
                  const isSelected = participantSet.has(String(character.id));
                  return (
                    <label key={character.id} className={styles.optionItem}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleParticipant(character.id)}
                      />
                      <span>{getCharacterLabel(character)}</span>
                    </label>
                  );
                }) : (
                  <p className={styles.hint}>Aucun personnage validé disponible dans cet univers.</p>
                )}
              </div>
            )}
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Factions impliquées</span>
            {loadingOptions ? (
              <p className={styles.hint}>Chargement des factions…</p>
            ) : (
              <div className={styles.optionList}>
                {factions.length > 0 ? factions.map((faction) => {
                  const isSelected = factionSet.has(String(faction.id));
                  return (
                    <label key={faction.id} className={styles.optionItem}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleFaction(faction.id)}
                      />
                      <span>{faction.name}</span>
                    </label>
                  );
                }) : (
                  <p className={styles.hint}>Aucune faction disponible dans cet univers.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={saving}>
          Annuler
        </button>
        <button type="submit" className={styles.btnPrimary} disabled={saving || loadingOptions}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

export default ThreadEditPanel;
